import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const [activeTab, setActiveTab] = useState<'intro' | 'reputation' | 'contact'>('intro');

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于香香漫画',
    description: '了解香香漫画网——韩漫日漫国漫免费在线阅读平台的品牌故事、用户口碑与联系方式。',
    url: 'https://www.xiangxiangmh.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '香香漫画',
      url: 'https://www.xiangxiangmh.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '香香漫画网有哪些类型的漫画？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '香香漫画网提供韩漫、日漫、国漫三大类型的免费漫画在线阅读，涵盖恋爱、奇幻、热血、修仙、校园、治愈等多种题材。',
        },
      },
      {
        '@type': 'Question',
        name: '香香漫画是否需要注册才能阅读？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不需要！香香漫画网所有漫画均可免费在线阅读，无需注册登录，打开即看，下拉式追更体验。',
        },
      },
      {
        '@type': 'Question',
        name: '香香漫画网的更新频率如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '香香漫画网每日更新大量漫画内容，热门连载漫画保持同步更新，确保读者不会错过任何精彩章节。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在手机上阅读香香漫画？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '香香漫画网采用响应式设计，完美适配手机、平板和电脑。只需在浏览器中打开香香漫画官网，即可享受流畅的下拉式阅读体验。',
        },
      },
    ],
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const tabs = [
    { key: 'intro' as const, label: '品牌介绍', icon: 'ri-building-line' },
    { key: 'reputation' as const, label: '用户口碑', icon: 'ri-heart-line' },
    { key: 'contact' as const, label: '联系我们', icon: 'ri-customer-service-line' },
  ];

  return (
    <>
      <SEO
        title="关于香香漫画 - 品牌介绍、口碑与联系方式 | 香香漫画网"
        description="了解香香漫画网——韩漫日漫国漫免费在线阅读平台。查看品牌介绍、用户口碑评价以及联系方式。安全直达，免费漫画大全。"
        keywords="香香漫画,关于香香漫画,漫画网站,品牌介绍,联系方式"
        canonicalUrl="https://www.xiangxiangmh.com/about"
        ogType="website"
        schema={{ ...aboutSchema }}
      />
      <ScrollToTop />
      <main className="pt-16">
        <Navbar />
        <Breadcrumb items={breadcrumbItems} />

        <section className="w-full max-w-[1000px] mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="text-center mb-10">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
              关于香香漫画
            </h1>
            <p className="text-sm text-foreground-500 max-w-xl mx-auto">
              致力于为漫画爱好者提供最优质的免费在线阅读体验
            </p>
          </div>

          <div className="flex items-center gap-1 mb-8 bg-background-100 rounded-full p-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.key
                    ? 'bg-background-50 text-foreground-900'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className={tab.icon}></i>
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'intro' && (
            <div className="space-y-8">
              <div className="bg-background-50 rounded-xl p-6 md:p-8">
                <h2 className="text-lg font-heading text-foreground-900 mb-4">品牌故事</h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    香香漫画网创立于2020年，是专注于中文漫画爱好者的免费在线阅读平台。我们深知漫画爱好者对于优质内容的渴望，因此致力于打造一个内容丰富、阅读体验流畅的漫画网站。
                  </p>
                  <p>
                    平台汇聚了海量的<strong>韩漫</strong>、<strong>日漫</strong>和<strong>国漫</strong>资源，涵盖恋爱、奇幻、热血、修仙、校园、治愈等多种题材。无论你是喜欢韩式唯美画风的韩漫迷，还是钟情于经典热血番的日漫爱好者，亦或是支持国产漫画的国漫粉，在香香漫画网都能找到心仪的作品。
                  </p>
                  <p>
                    我们采用先进的<strong>下拉式阅读</strong>技术，用户无需翻页，只需轻轻向下滑动即可流畅阅读，大大提升了移动端的阅读体验。同时，网站完全免费，无需注册登录，打开即看，真正做到"好看的漫画免费全集大全"。
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
                <div className="bg-background-50 rounded-xl p-5 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                    <i className="ri-book-open-line text-xl text-primary-600"></i>
                  </div>
                  <h3 className="font-semibold text-foreground-900 text-sm mb-1">海量资源</h3>
                  <p className="text-xs text-foreground-500">汇聚韩漫日漫国漫，每日更新不停歇</p>
                </div>
                <div className="bg-background-50 rounded-xl p-5 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
                    <i className="ri-flashlight-line text-xl text-accent-600"></i>
                  </div>
                  <h3 className="font-semibold text-foreground-900 text-sm mb-1">免费阅读</h3>
                  <p className="text-xs text-foreground-500">无需注册无需付费，全部漫画免费看</p>
                </div>
                <div className="bg-background-50 rounded-xl p-5 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-100">
                    <i className="ri-smartphone-line text-xl text-secondary-600"></i>
                  </div>
                  <h3 className="font-semibold text-foreground-900 text-sm mb-1">流畅体验</h3>
                  <p className="text-xs text-foreground-500">下拉式阅读，多端适配，随时随地追漫</p>
                </div>
              </div>

              <div className="bg-background-50 rounded-xl p-6 md:p-8">
                <h2 className="text-lg font-heading text-foreground-900 mb-4">我们的使命</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  香香漫画网的使命是让每一位漫画爱好者都能轻松畅享优质漫画内容。我们坚持免费、开放的理念，不断扩充漫画资源库，优化阅读体验，努力成为中国最受欢迎的漫画在线阅读平台之一。未来，我们还将推出更多个性化功能，让追漫之旅更加精彩！
                </p>
              </div>
            </div>
          )}

          {activeTab === 'reputation' && (
            <div className="space-y-6">
              <div className="bg-background-50 rounded-xl p-6 md:p-8">
                <h2 className="text-lg font-heading text-foreground-900 mb-4">用户评价</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    {
                      name: '小明同学',
                      rating: 5,
                      comment: '香香漫画真的太棒了！画质清晰，更新速度快，韩漫资源特别全，每天都在上面追漫。下拉式阅读真的很方便，推荐给所有喜欢看漫画的朋友！',
                      date: '2026-07-28',
                    },
                    {
                      name: '漫画迷小王',
                      rating: 5,
                      comment: '用了很多漫画网站，香香漫画的体验是最好的。界面简洁好看，没有乱七八糟的广告，漫画分类也很清晰。国漫和日漫资源都很丰富，强烈推荐！',
                      date: '2026-07-25',
                    },
                    {
                      name: '樱花飘落',
                      rating: 5,
                      comment: '作为一个重度日漫爱好者，在香香漫画找到了好多喜欢的作品。不用注册就能看，这点太贴心了。追更功能也很好用，有新章节更新就能第一时间看到。',
                      date: '2026-07-22',
                    },
                    {
                      name: '追风少年',
                      rating: 4,
                      comment: '国漫崛起，在香香漫画上看了很多优秀的国产漫画。希望平台能继续丰富国漫资源，支持国产！整体阅读体验非常不错，下拉式用起来很顺手。',
                      date: '2026-07-20',
                    },
                    {
                      name: '夜雨声烦',
                      rating: 5,
                      comment: '最喜欢韩漫的唯美画风，香香漫画上的韩漫资源比其他网站全多了。更新也很及时，基本上热门漫画都是同步更新的。手机上看体验也很好！',
                      date: '2026-07-18',
                    },
                    {
                      name: '书中自有黄金屋',
                      rating: 5,
                      comment: '香香漫画是我每天的快乐源泉！无论是通勤路上还是睡前，都会打开刷几话。漫画种类齐全，从恋爱甜宠到热血冒险，总有一款适合你！',
                      date: '2026-07-15',
                    },
                  ].map((review, index) => (
                    <div key={index} className="bg-background-100 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-sm text-foreground-800">{review.name}</span>
                        <span className="text-xs text-foreground-400">{review.date}</span>
                      </div>
                      <div className="flex items-center gap-0.5 mb-2">
                        {Array.from({ length: review.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                        ))}
                        {Array.from({ length: 5 - review.rating }).map((_, i) => (
                          <i key={i} className="ri-star-line text-foreground-300 text-xs"></i>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-background-50 rounded-xl p-6 md:p-8">
                <div className="flex items-center gap-6 flex-wrap">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary-500 font-heading">500万+</div>
                    <div className="text-xs text-foreground-500 mt-1">月活跃用户</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-accent-500 font-heading">4.8</div>
                    <div className="text-xs text-foreground-500 mt-1">用户平均评分</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-secondary-500 font-heading">99.7%</div>
                    <div className="text-xs text-foreground-500 mt-1">好评率</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="space-y-8">
              <div className="bg-background-50 rounded-xl p-6 md:p-8">
                <h2 className="text-lg font-heading text-foreground-900 mb-6">联系方式</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-800 mb-1">电子邮箱</h3>
                      <p className="text-sm text-foreground-500">contact@xiangxiangmh.com</p>
                      <p className="text-xs text-foreground-400 mt-0.5">商务合作 / 版权事宜</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100">
                      <i className="ri-customer-service-line text-accent-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-800 mb-1">在线客服</h3>
                      <p className="text-sm text-foreground-500">工作日 9:00 - 18:00</p>
                      <p className="text-xs text-foreground-400 mt-0.5">用户反馈 / 技术支持</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-100">
                      <i className="ri-map-pin-line text-secondary-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-800 mb-1">公司地址</h3>
                      <p className="text-sm text-foreground-500">中国北京市朝阳区</p>
                      <p className="text-xs text-foreground-400 mt-0.5">互联网产业园区</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className="ri-wechat-line text-primary-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-800 mb-1">微信公众号</h3>
                      <p className="text-sm text-foreground-500">香香漫画</p>
                      <p className="text-xs text-foreground-400 mt-0.5">关注获取最新漫讯</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 rounded-xl p-6 md:p-8">
                <h2 className="text-lg font-heading text-foreground-900 mb-4">常见问题</h2>
                <div className="space-y-4">
                  {[
                    {
                      q: '香香漫画网有哪些类型的漫画？',
                      a: '香香漫画网提供韩漫、日漫、国漫三大类型的免费漫画在线阅读，涵盖恋爱、奇幻、热血、修仙、校园、治愈等多种题材。',
                    },
                    {
                      q: '香香漫画是否需要注册才能阅读？',
                      a: '不需要！香香漫画网所有漫画均可免费在线阅读，无需注册登录，打开即看，下拉式追更体验。',
                    },
                    {
                      q: '香香漫画网的更新频率如何？',
                      a: '香香漫画网每日更新大量漫画内容，热门连载漫画保持同步更新，确保读者不会错过任何精彩章节。',
                    },
                    {
                      q: '如何在手机上阅读香香漫画？',
                      a: '香香漫画网采用响应式设计，完美适配手机、平板和电脑。只需在浏览器中打开香香漫画官网，即可享受流畅的下拉式阅读体验。',
                    },
                    {
                      q: '如果发现漫画内容有问题，如何反馈？',
                      a: '您可以通过在线客服或发送邮件至 contact@xiangxiangmh.com 与我们联系，我们会在24小时内处理您的反馈。',
                    },
                  ].map((faq, index) => (
                    <details key={index} className="group bg-background-100 rounded-lg">
                      <summary className="px-5 py-4 text-sm font-medium text-foreground-800 cursor-pointer flex items-center justify-between">
                        {faq.q}
                        <i className="ri-arrow-down-s-line transition-transform duration-200 group-open:rotate-180 text-foreground-400"></i>
                      </summary>
                      <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                        {faq.a}
                      </div>
                    </details>
                  ))}
                </div>
              </div>

              <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
            </div>
          )}
        </section>

        <Footer />
      </main>
    </>
  );
}